/* Print tab: the two printable sheets, their controls, and a preview of the
 * actual PDF.
 *
 * The preview is an <iframe> pointed at the same endpoint the download button
 * uses, so what an agent looks at on screen is byte-for-byte the file they
 * will print. A separate preview renderer would be faster and would
 * eventually disagree with the real thing, which is the bug you find out
 * about at the print shop.
 *
 * Colours and type are shared by both sheets on purpose: the flyer and the
 * open-house sheet sit on the same table at the same open house, and nobody
 * wants to restyle twice to keep them matching. Photos and wording are per
 * sheet, because they genuinely differ -- four small photos on one, two big
 * ones on the other.
 *
 * Every control writes through to the server after a pause and then bumps
 * `version`, which re-fetches the iframe. No local mirror of the layout:
 * reportlab is the only thing that decides what a sheet looks like.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Download, RefreshCw, Image as ImageIcon } from "lucide-react";
import { api, API, formatApiError } from "../../lib/api";

/* Thumbnail for the photo pickers. Deliberately a local copy rather than a
   shared component: the two existing versions live inside page files that
   import this one, and wiring round that circle is a bigger change than one
   small duplicated fetch. Worth extracting into lib/ when a fourth appears. */
const urls = new Map();
const inflight = new Map();

function thumbUrl(photoId) {
  if (urls.has(photoId)) return Promise.resolve(urls.get(photoId));
  if (inflight.has(photoId)) return inflight.get(photoId);
  const p = api
    .get(`/listing-photos/${photoId}/file`, { responseType: "blob" })
    .then(({ data }) => {
      const u = URL.createObjectURL(data);
      urls.set(photoId, u);
      inflight.delete(photoId);
      return u;
    })
    .catch((e) => {
      inflight.delete(photoId);
      throw e;
    });
  inflight.set(photoId, p);
  return p;
}

function Thumb({ photoId, style }) {
  const [src, setSrc] = useState(() => urls.get(photoId) || "");
  useEffect(() => {
    if (!photoId || urls.has(photoId)) {
      setSrc(urls.get(photoId) || "");
      return;
    }
    let alive = true;
    thumbUrl(photoId)
      .then((u) => alive && setSrc(u))
      .catch(() => {});
    return () => { alive = false; };
  }, [photoId]);
  if (!src) return <div style={{ ...style, background: "var(--surface-alt)" }} />;
  return <img src={src} alt="" style={style} />;
}

const FIELD = { fontSize: 13, fontWeight: 500, display: "block", margin: "14px 0 6px" };
const HINT = { fontSize: 12, color: "var(--text-muted)", marginTop: 5 };
const SECTION = { marginTop: 20, borderTop: "1px solid var(--border)", paddingTop: 16 };

const SHEETS = [
  { id: "flyer", label: "Single-page flyer", file: "flyer.pdf",
    blurb: "8.5 × 11, ready to print. Everything below starts from this listing and your branding — change what you like for this house only." },
  { id: "open_house", label: "Open house", file: "open-house.pdf",
    blurb: "The sheet people pick up on the day. Same house, bigger type, and the date across the top." },
];

export default function PrintTab({ sellerId, setToast }) {
  const [opts, setOpts] = useState(null);
  const [state, setState] = useState(null);    // { flyer, open_house, available_stats, photo_ids }
  const [sheet, setSheet] = useState("flyer");
  const [version, setVersion] = useState(0);
  const [saving, setSaving] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    api.get("/admin/flyer-options").then(({ data }) => setOpts(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!sellerId) return;
    api
      .get(`/admin/seller/${sellerId}/flyer`)
      .then(({ data }) => setState(data))
      .catch((err) => setToast(formatApiError(err?.response?.data?.detail) || "Couldn't load the flyer"));
  }, [sellerId, setToast]);

  // write() needs the latest settings without re-creating itself on every keystroke.
  const stateRef = useRef(state);
  useEffect(() => { stateRef.current = state; }, [state]);

  /* One writer for every control. Updates the draft immediately so the
     controls stay responsive, then saves after a pause and refreshes the
     preview once the server has agreed what the sheet is. `which` is the
     sheet being written, which is not always the sheet on screen: colours
     and type belong to the flyer and are read by both. */
  const write = useCallback(
    (patch, which) => {
      const key = which || "flyer";
      setState((s) => (s ? { ...s, [key]: { ...s[key], ...patch } } : s));
      clearTimeout(timer.current);
      timer.current = setTimeout(async () => {
        setSaving(true);
        try {
          const body = { ...(stateRef.current?.[key] || {}), ...patch };
          const { data } = await api.put(`/admin/seller/${sellerId}/flyer`, { [key]: body });
          setState((s) => (s ? { ...s, [key]: data[key] } : s));
          setVersion((v) => v + 1);
        } catch (err) {
          setToast(formatApiError(err?.response?.data?.detail) || "Couldn't save that change");
        } finally {
          setSaving(false);
        }
      }, 600);
    },
    [sellerId, setToast],
  );

  if (!state || !opts) {
    return <div style={{ padding: 28, color: "var(--text-muted)" }}>Loading…</div>;
  }

  const isOH = sheet === "open_house";
  const meta = SHEETS.find((s) => s.id === sheet) || SHEETS[0];
  const f = (isOH ? state.open_house : state.flyer) || {};
  const style = state.flyer?.style || {};
  const photos = state.photo_ids || [];
  const statKeys = Object.keys(state.available_stats || {});
  const maxThumbs = isOH ? (opts.max_oh_thumbs || 2) : (opts.max_thumbs || 4);
  const maxStats = isOH ? (opts.max_oh_stats || 4) : (opts.max_stats || 5);
  const pdfUrl = `${API}/admin/seller/${sellerId}/${meta.file}?v=${version}`;

  const put = (patch) => write(patch, sheet);
  const setStyle = (patch) => write({ style: { ...style, ...patch } }, "flyer");

  const toggleThumb = (pid) => {
    const cur = f.thumb_photo_ids || [];
    const next = cur.includes(pid)
      ? cur.filter((x) => x !== pid)
      : cur.length >= maxThumbs
        ? cur
        : [...cur, pid];
    if (next === cur) {
      setToast(`${maxThumbs} supporting photo${maxThumbs === 1 ? "" : "s"} is the maximum on this sheet.`);
      return;
    }
    put({ thumb_photo_ids: next });
  };

  const toggleStat = (key) => {
    const cur = f.stats || [];
    const next = cur.includes(key)
      ? cur.filter((x) => x !== key)
      : cur.length >= maxStats
        ? cur
        : [...cur, key];
    if (next === cur) { setToast(`${maxStats} stats is as many as fit across the row.`); return; }
    put({ stats: next });
  };

  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,420px)", gap: 24 }}>
      {/* ---------------------------------------------------- controls */}
      <div className="card" style={{ padding: 24 }}>
        <div style={{ display: "flex", gap: 7, marginBottom: 16 }}>
          {SHEETS.map((s) => (
            <button
              key={s.id}
              type="button"
              data-testid={`print-sheet-${s.id}`}
              onClick={() => setSheet(s.id)}
              style={{
                cursor: "pointer", fontSize: 13, padding: "8px 15px", borderRadius: 999,
                background: sheet === s.id ? "var(--primary)" : "none",
                color: sheet === s.id ? "#fff" : "var(--text)",
                border: sheet === s.id ? "1px solid var(--primary)" : "1px solid var(--border)",
              }}
            >
              {s.label}
            </button>
          ))}
        </div>

        <div className="overline">{meta.label}</div>
        <div style={{ ...HINT, marginBottom: 4 }}>{meta.blurb}</div>

        {/* photos */}
        <div style={SECTION}>
          <div className="overline">Photos</div>
          {photos.length === 0 ? (
            <div style={{ ...HINT, display: "flex", gap: 8, alignItems: "center", marginTop: 8 }}>
              <ImageIcon size={14} /> No listing photos yet — add some on the Social Posts tab and
              they'll show up here.
            </div>
          ) : (
            <>
              <label style={FIELD}>Main photo</label>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(76px, 1fr))", gap: 8 }}>
                {photos.map((pid) => {
                  const on = pid === f.hero_photo_id;
                  return (
                    <button
                      key={pid}
                      type="button"
                      data-testid={`flyer-hero-${pid}`}
                      onClick={() => put({ hero_photo_id: pid })}
                      style={{
                        padding: 0, cursor: "pointer", background: "none", borderRadius: 8,
                        overflow: "hidden", border: on ? "2px solid var(--primary)" : "1px solid var(--border)",
                      }}
                    >
                      <Thumb photoId={pid} style={{ width: "100%", height: 56, objectFit: "cover", display: "block" }} />
                    </button>
                  );
                })}
              </div>

              <label style={FIELD}>
                Smaller photos{" "}
                <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>
                  ({(f.thumb_photo_ids || []).length} of {maxThumbs}, in order)
                </span>
              </label>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(76px, 1fr))", gap: 8 }}>
                {photos.map((pid) => {
                  const idx = (f.thumb_photo_ids || []).indexOf(pid);
                  return (
                    <button
                      key={pid}
                      type="button"
                      data-testid={`flyer-thumb-${pid}`}
                      onClick={() => toggleThumb(pid)}
                      style={{
                        position: "relative", padding: 0, cursor: "pointer", background: "none",
                        borderRadius: 8, overflow: "hidden",
                        border: idx >= 0 ? "2px solid var(--primary)" : "1px solid var(--border)",
                        opacity: idx >= 0 ? 1 : 0.62,
                      }}
                    >
                      <Thumb photoId={pid} style={{ width: "100%", height: 56, objectFit: "cover", display: "block" }} />
                      {idx >= 0 && (
                        <span style={{
                          position: "absolute", top: 4, left: 4, background: "var(--primary)", color: "#fff",
                          fontSize: 10, fontWeight: 600, width: 17, height: 17, borderRadius: 999,
                          display: "grid", placeItems: "center",
                        }}>{idx + 1}</span>
                      )}
                    </button>
                  );
                })}
              </div>
              {isOH && (
                <div style={HINT}>
                  Two, and printed large — this one gets read from across a room.
                </div>
              )}
            </>
          )}
        </div>

        {/* colours — shared */}
        <div style={SECTION}>
          <div className="overline">Colours</div>
          <div style={{ ...HINT, marginTop: 0 }}>Used by both sheets, so they match on the table.</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
            {(opts.color_presets || []).map((p) => {
              const on = p.band === style.band && p.accent === style.accent;
              return (
                <button
                  key={p.id}
                  type="button"
                  data-testid={`flyer-preset-${p.id}`}
                  onClick={() => setStyle({ band: p.band, accent: p.accent })}
                  title={p.label}
                  style={{
                    display: "flex", alignItems: "center", gap: 8, cursor: "pointer",
                    padding: "6px 11px 6px 7px", borderRadius: 999, background: "none",
                    border: on ? "2px solid var(--primary)" : "1px solid var(--border)",
                    fontSize: 12.5,
                  }}
                >
                  <span style={{ display: "flex" }}>
                    <span style={{ width: 15, height: 15, borderRadius: "50% 0 0 50%", background: p.band }} />
                    <span style={{ width: 15, height: 15, borderRadius: "0 50% 50% 0", background: p.accent }} />
                  </span>
                  {p.label}
                </button>
              );
            })}
          </div>
          <div style={{ display: "flex", gap: 18, marginTop: 14 }}>
            <label style={{ fontSize: 12.5 }}>
              <span style={{ display: "block", color: "var(--text-muted)", marginBottom: 5 }}>Band</span>
              <input type="color" data-testid="flyer-band" value={style.band || "#2B4C3F"}
                     onChange={(e) => setStyle({ band: e.target.value })}
                     style={{ width: 54, height: 30, border: "1px solid var(--border)", borderRadius: 6, background: "none" }} />
            </label>
            <label style={{ fontSize: 12.5 }}>
              <span style={{ display: "block", color: "var(--text-muted)", marginBottom: 5 }}>Accent</span>
              <input type="color" data-testid="flyer-accent" value={style.accent || "#B85C47"}
                     onChange={(e) => setStyle({ accent: e.target.value })}
                     style={{ width: 54, height: 30, border: "1px solid var(--border)", borderRadius: 6, background: "none" }} />
            </label>
          </div>
          <div style={HINT}>
            The band is the strip at the foot; the accent is the price and the line under it.
            A pale band keeps its colour — the text on it flips dark so it stays readable.
          </div>
        </div>

        {/* type — shared */}
        <div style={SECTION}>
          <div className="overline">Type</div>
          <select className="input-field" data-testid="flyer-font" style={{ marginTop: 10 }}
                  value={style.font_pair || "classic"}
                  onChange={(e) => setStyle({ font_pair: e.target.value })}>
            {(opts.font_pairs || []).map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
          <div style={HINT}>Chosen in pairs so the headline and the body always suit each other. Both sheets follow it.</div>
        </div>

        {/* words and stats */}
        <div style={SECTION}>
          <div className="overline">Words</div>

          {isOH && (
            <>
              <label style={FIELD}>Date</label>
              <input className="input-field" data-testid="oh-date" value={f.date || ""} maxLength={46}
                     onChange={(e) => put({ date: e.target.value })} placeholder="Saturday, September 27" />
              <label style={FIELD}>Time</label>
              <input className="input-field" data-testid="oh-time" value={f.time || ""} maxLength={36}
                     onChange={(e) => put({ time: e.target.value })} placeholder="1:00 – 4:00 PM" />
              <div style={HINT}>
                Typed rather than picked from a calendar — "Saturday &amp; Sunday" and "by appointment"
                are both things agents write, and neither survives a date field.
              </div>
              <label style={FIELD}>Line under the banner</label>
              <input className="input-field" data-testid="oh-kicker" value={f.kicker || ""} maxLength={48}
                     onChange={(e) => put({ kicker: e.target.value })} placeholder="Come take a look inside" />
            </>
          )}

          <label style={FIELD}>Address</label>
          <input className="input-field" data-testid="flyer-address" value={f.address || ""}
                 onChange={(e) => put({ address: e.target.value })} placeholder="1428 Juniper Way" />
          <label style={FIELD}>City, state, zip</label>
          <input className="input-field" data-testid="flyer-city" value={f.city || ""}
                 onChange={(e) => put({ city: e.target.value })} placeholder="Provo, Utah 84604" />
          <label style={FIELD}>Price</label>
          <input className="input-field" data-testid="flyer-price" value={f.price || ""}
                 onChange={(e) => put({ price: e.target.value })} placeholder="$675,000" />

          {!isOH && (
            <>
              <label style={FIELD}>Description</label>
              <textarea className="input-field" data-testid="flyer-description" rows={4}
                        value={f.description || ""} onChange={(e) => put({ description: e.target.value })}
                        placeholder="A quiet street, a west-facing porch…" />
              <div style={HINT}>
                Starts from what your seller wrote on the Listing Input form. Long ones are set smaller
                rather than pushed onto a second page.
              </div>
            </>
          )}

          {isOH && (
            <>
              <label style={FIELD}>Sign-in line</label>
              <textarea className="input-field" data-testid="oh-signin" rows={3} maxLength={220}
                        value={f.signin || ""} onChange={(e) => put({ signin: e.target.value })}
                        placeholder="Scan for photos, the floor plan and everything else about this home." />
              <div style={HINT}>
                {state.qr_ready
                  ? "Printed next to a QR code pointing at this home's listing page."
                  : "The QR code appears once this home's listing page is published — until then this line prints on its own."}
              </div>
            </>
          )}

          <label style={FIELD}>
            Stats{" "}
            <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>
              ({(f.stats || []).length} of {maxStats}, in order)
            </span>
          </label>
          {statKeys.length === 0 ? (
            <div style={HINT}>Nothing to show yet — these come from the Listing Input form.</div>
          ) : (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
              {statKeys.map((k) => {
                const idx = (f.stats || []).indexOf(k);
                const s = state.available_stats[k];
                return (
                  <button key={k} type="button" data-testid={`flyer-stat-${k}`} onClick={() => toggleStat(k)}
                          style={{
                            cursor: "pointer", fontSize: 12.5, padding: "6px 11px", borderRadius: 999,
                            background: idx >= 0 ? "var(--primary)" : "none",
                            color: idx >= 0 ? "#fff" : "var(--text)",
                            border: idx >= 0 ? "1px solid var(--primary)" : "1px solid var(--border)",
                          }}>
                    {s.label} <span style={{ opacity: 0.7 }}>{s.value}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div style={{ ...HINT, marginTop: 20, borderTop: "1px solid var(--border)", paddingTop: 14 }}>
          Your name, brokerage, licence and logo come from Content → Branding, and the fair-housing
          line is always on the sheet. Neither is editable here — they're the things on a printed
          sheet that shouldn't depend on remembering.
        </div>
      </div>

      {/* ----------------------------------------------------- preview */}
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <div className="overline" style={{ margin: 0 }}>Preview</div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {saving && (
              <span style={{ fontSize: 12, color: "var(--text-muted)", display: "flex", gap: 5, alignItems: "center" }}>
                <RefreshCw size={12} /> Saving…
              </span>
            )}
            <a className="btn-primary" data-testid="flyer-download" href={pdfUrl} download
               style={{ display: "inline-flex", alignItems: "center", gap: 7, textDecoration: "none" }}>
              <Download size={14} /> Download PDF
            </a>
          </div>
        </div>
        <iframe
          key={`${sheet}-${version}`}
          title={`${meta.label} preview`}
          data-testid="flyer-preview"
          src={`${pdfUrl}#toolbar=0&navpanes=0`}
          style={{
            width: "100%", aspectRatio: "8.5 / 11", border: "1px solid var(--border)",
            borderRadius: 10, background: "var(--surface-alt)",
          }}
        />
        <div style={HINT}>This is the file itself, not a mock-up of it.</div>
      </div>
    </div>
  );
}
